import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ShieldCheck, Pencil, Loader2, Mail, Lock, User, Users, Layers, ArrowRight, Users as UsersIcon, Check, KeyRound, Copy, FileDown, Presentation, FileText, FolderOpen, Wand2, TrendingUp, Activity, Sparkles, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { AppShell } from '../components/Layout';
import { adminApi, downloadAdminAuditReport, api } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { useAuth } from '../lib/auth-context';
import { Navigate, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { WritingStyleCard } from '../components/admin/WritingStyleCard';
import { TrackingScriptsPanel } from '../components/admin/TrackingScriptsPanel';
import { ConsentLogPanel } from '../components/admin/ConsentLogPanel';
import { AdminInspirationsPanel } from '../components/admin/AdminInspirationsPanel';
import { AdminExpertPreferencesPanel } from '../components/admin/AdminExpertPreferencesPanel';
import { AdminAnalyzeByUrlPanel } from '../components/admin/AdminAnalyzeByUrlPanel';
import { SendCompAuditDialog } from '../components/admin/SendCompAuditDialog';
import { RoleBadge } from '../components/admin/RoleBadge';
import { SupportTicketsPanel } from '../components/admin/SupportTicketsPanel';
import { BillingProvisionsPanel } from '../components/admin/BillingProvisionsPanel';
import { PostWriterTrainerPanel } from '../components/admin/PostWriterTrainerPanel';
import { FreeScoreAuditsPanel } from '../components/admin/FreeScoreAuditsPanel';
import { FreeScorePdfLeadsPanel } from '../components/admin/FreeScorePdfLeadsPanel';
import { CollapsibleAdminSection } from '../components/admin/CollapsibleAdminSection';

const roleBadge = (role) => {
  if (role === 'admin') return 'bg-primary text-primary-foreground';
  if (role === 'editor') return 'bg-[hsl(var(--sky))] text-foreground border border-border';
  return 'bg-secondary text-foreground border border-border';
};

export default function AdminPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingReportFor, setExportingReportFor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null); // {email, temp_password}
  const [compAuditTarget, setCompAuditTarget] = useState(null);
  // Bulk-send state: set of selected user_ids + open bulk-send dialog.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // last-send result envelope
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [pendingResets, setPendingResets] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ items }, requestsResp] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listPasswordResetRequests().catch(() => ({ requests: [] })),
      ]);
      setUsers(items || []);
      setPendingResets(requestsResp?.requests || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load users'));
    } finally {
      setLoading(false);
    }
  };

  const onConfirmReset = async () => {
    if (!resetTarget) return;
    setResetSubmitting(true);
    try {
      const data = await adminApi.resetUserPassword(resetTarget.id);
      setResetResult(data);
      setResetTarget(null);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not reset password'));
    } finally {
      setResetSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (_e) {
      toast.error('Could not copy');
    }
  };

  const onExportBrandedReport = async (targetUser, tier = 'full', format = 'pdf') => {
    if (!targetUser?.id) return;
    if (exportingReportFor) return;
    setExportingReportFor(targetUser.id);
    try {
      const label = targetUser.name || targetUser.email || 'user';
      const niceTier = tier === 'partial' ? 'preview' : 'full report';
      const niceFmt = format === 'pptx' ? 'PPTX' : 'PDF';
      toast.info(`Generating ${niceTier} (${niceFmt})...`);
      await downloadAdminAuditReport(targetUser.id, label, tier, format);
      toast.success(`${niceTier === 'preview' ? 'Preview' : 'Full report'} (${niceFmt}) downloaded for ${label}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not export branded report'));
    } finally {
      setExportingReportFor(null);
    }
  };

  // Bulk send helpers.
  const toggleSelected = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };
  // Users that CAN receive a comp audit — must have >= 1 completed analysis.
  const bulkEligibleUsers = users.filter((u) => (u.analyses_count || 0) > 0);
  const allBulkSelected = bulkEligibleUsers.length > 0
    && bulkEligibleUsers.every((u) => selectedIds.has(u.id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (bulkEligibleUsers.every((u) => prev.has(u.id))) return new Set();
      const next = new Set(prev);
      bulkEligibleUsers.forEach((u) => next.add(u.id));
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());
  const submitBulkSend = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const { data } = await api.post('/admin/users/bulk-send-comp-audit', {
        user_ids: ids,
        message: bulkMessage.trim() || undefined,
      });
      setBulkResult(data);
      const sent = data?.sent || 0;
      const total = data?.total || ids.length;
      if (sent === total) {
        toast.success(`Comp audit sent to all ${sent} recipient${sent === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Sent to ${sent} of ${total} — check details below`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Bulk send failed'));
    } finally {
      setBulkSending(false);
    }
  };
  const closeBulkDialog = () => {
    if (bulkSending) return;
    setBulkOpen(false);
    setBulkMessage('');
    setBulkResult(null);
    // Keep selectedIds intact so the admin can retry any failed rows
    // without re-selecting from scratch. Clear them via the toolbar.
  };

  useEffect(() => {
    // Force a fresh /auth/me call whenever the AdminPage mounts so a
    // just-granted user (whose token was minted BEFORE the grant) picks
    // up their new admin_features without a full logout/login cycle.
    if (typeof refresh === 'function') refresh();
    reload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;
  const hasAdminFeatureGrant = Array.isArray(user.admin_features) && user.admin_features.length > 0;
  if (user.role !== 'admin' && user.role !== 'editor' && !hasAdminFeatureGrant) {
    return <Navigate to="/dashboard" replace />;
  }
  const isAdmin = user.role === 'admin';
  const isEditor = user.role === 'editor';
  const isGrantOnly = !isAdmin && !isEditor;
  const panelLabel = isAdmin ? 'Admin panel' : isEditor ? 'Editor panel' : 'Delegated access';
  const pageTitle = isAdmin ? 'Team & access' : isEditor ? 'Your profiles & audits' : 'Delegated tools';
  const pageBlurb = isAdmin
    ? 'Manage who can use Blayzrr and what they can do.'
    : isEditor
      ? 'Download branded audit reports for the profiles you uploaded and the users assigned to you.'
      : 'You have been granted access to the tools below by an administrator.';

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Badge className="bg-card border border-border text-foreground mb-2" data-testid="admin-eyebrow">
              <ShieldCheck size={12} className="mr-1.5 text-primary" />
              {panelLabel}
            </Badge>
            <h1 className="text-3xl font-bold">
              {pageTitle}
            </h1>
            <p className="text-muted-foreground mt-1">
              {pageBlurb}
            </p>
          </div>
          {/* Editor-visible primary link. Admins get their own richer
              nav block below with everything else. */}
          {!isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => navigate('/admin/events')}
                className="rounded-xl gap-1.5"
                data-testid="editor-events-link"
              >
                <Activity size={14} /> All events
              </Button>
              {(user.admin_features || []).includes('hero_leads') && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/hero-leads')}
                  className="rounded-xl gap-1.5"
                  data-testid="editor-hero-leads-link"
                >
                  <Users size={14} /> Hero leads
                </Button>
              )}
              {(user.admin_features || []).includes('free_leads') && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/free-leads')}
                  className="rounded-xl gap-1.5"
                  data-testid="editor-free-leads-link"
                >
                  <Users size={14} /> Free leads
                </Button>
              )}
            </div>
          )}
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => navigate('/admin/reports')}
                className="rounded-xl gap-1.5"
                data-testid="admin-reports-link"
              >
                <TrendingUp size={14} /> Reports
              </Button>
              <Button
                onClick={() => navigate('/admin/events')}
                variant="outline"
                className="rounded-xl gap-1.5"
                data-testid="admin-events-link"
              >
                <Activity size={14} /> All events
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/support')}
                className="rounded-xl gap-1.5"
                data-testid="admin-support-link"
              >
                <Mail size={14} /> Support inbox
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/apify-usage')}
                className="rounded-xl gap-1.5"
                data-testid="admin-apify-usage-link"
              >
                <FolderOpen size={14} /> Apify usage
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/hero-leads')}
                className="rounded-xl gap-1.5"
                data-testid="admin-hero-leads-link"
              >
                <Users size={14} /> Hero leads
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/free-leads')}
                className="rounded-xl gap-1.5"
                data-testid="admin-free-leads-link"
              >
                <Users size={14} /> Free leads
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/support-inbox')}
                className="rounded-xl gap-1.5"
                data-testid="admin-support-inbox-link"
              >
                <Users size={14} /> Support inbox
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/discover')}
                className="rounded-xl gap-1.5"
                data-testid="admin-discover-link"
              >
                <Users size={14} /> Discover peers &amp; topics
              </Button>
              <Button onClick={() => setAddOpen(true)} className="rounded-xl" data-testid="admin-add-user-button">
                <Plus size={16} className="mr-2" /> Add user
              </Button>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="grid sm:grid-cols-3 gap-3">
            <RoleSummary role="admin" count={users.filter((u) => u.role === 'admin').length} desc="Full access — manage users, see and edit all analyses." />
            <RoleSummary role="editor" count={users.filter((u) => u.role === 'editor').length} desc="See own + analyses for users assigned to them by an admin." />
            <RoleSummary role="viewer" count={users.filter((u) => u.role === 'viewer').length} desc="Only sees their own analyses." />
          </div>
        )}

        {/* Delegated access diagnostic strip — always visible for
            non-admin users so they (and support) can see exactly which
            grants the current session carries. If a grant is missing here,
            either the admin never ticked it OR the session token is stale
            (log out + back in). */}
        {!isAdmin && (
          <Card className="border-border/60">
            <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1">
                  Your access
                </div>
                <div className="text-sm font-medium">
                  Role: <span className="font-mono">{user.role || 'viewer'}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {Array.isArray(user.admin_features) && user.admin_features.length > 0
                    ? `Delegated grants: ${user.admin_features.join(', ')}`
                    : 'No delegated grants on this session. If your admin just added one, log out and back in.'}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => refresh?.()}
                data-testid="admin-refresh-session"
              >
                Refresh session
              </Button>
            </div>
          </Card>
        )}

        <Card
          className="border-primary/30 bg-gradient-to-br from-[hsl(var(--mint))]/30 via-card to-[hsl(var(--sky))]/20 cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => navigate('/dashboard')}
          data-testid="admin-manage-analyses-card"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/dashboard');
            }
          }}
        >
          <CardContent className="p-5 flex items-center gap-4">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
              <Layers size={18} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono-edge text-primary">
                {isAdmin ? 'CONTENT OVERSIGHT' : 'YOUR CONTENT'}
              </p>
              <h2 className="font-semibold mt-0.5">
                {isAdmin ? 'Manage all analyses & posts' : 'View your analyses & posts'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isAdmin
                  ? "View, edit, regenerate, or delete any user's profile analysis. Edit voice profile, post context, individual generated posts, or remove them entirely."
                  : 'See the analyses you can access. Audit downloads are available below.'}
              </p>
            </div>
            <ArrowRight size={18} className="text-primary shrink-0" />
          </CardContent>
        </Card>

        {isAdmin && pendingResets.length > 0 && (
          <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-500/5" data-testid="admin-pending-resets-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-amber-500/30 flex items-center gap-2">
                <KeyRound size={18} className="text-amber-600 dark:text-amber-400" />
                <h2 className="font-semibold">Pending password reset requests</h2>
                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 font-mono-edge text-[11px] ml-1" data-testid="admin-pending-resets-count">
                  {pendingResets.length}
                </Badge>
                <span className="text-sm text-muted-foreground ml-auto hidden sm:block">
                  Users who clicked &ldquo;Forgot password&rdquo;
                </span>
              </div>
              <Table data-testid="admin-pending-resets-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingResets.map((req) => {
                    const matchedUser = users.find((u) => u.id === req.user_id || u.email === req.email);
                    return (
                      <TableRow key={req.id} data-testid={`admin-pending-reset-row-${req.email}`}>
                        <TableCell className="font-medium">{req.email}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {req.created_at ? format(new Date(req.created_at), "MMM d, yyyy 'at' h:mm a") : ''}
                        </TableCell>
                        <TableCell className="text-right">
                          {matchedUser ? (
                            <Button
                              size="sm"
                              className="rounded-lg"
                              onClick={() => setResetTarget(matchedUser)}
                              data-testid={`admin-pending-reset-action-${req.email}`}
                            >
                              <KeyRound size={13} className="mr-1.5" />
                              Reset password
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">User no longer exists</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {isAdmin && <WritingStyleCard />}

        {(isAdmin || (user.admin_features || []).includes('analyze_by_url')) && (
          <CollapsibleAdminSection
            title="Audit by URL"
            description="Paste any LinkedIn URL and generate the full audit + rewrite. Bypasses billing quotas."
            icon={<Wand2 size={18} className="text-primary" />}
            testId="admin-section-analyze-by-url"
          >
            <AdminAnalyzeByUrlPanel />
          </CollapsibleAdminSection>
        )}

        {isAdmin && <BillingProvisionsPanel />}
        {isAdmin && (
          <CollapsibleAdminSection
            title="Free-score PDF leads"
            description="Legacy: emails captured from the free PDF audit funnel."
            icon={<FileText size={18} className="text-primary" />}
            testId="admin-section-free-pdf-leads"
          >
            <FreeScorePdfLeadsPanel />
          </CollapsibleAdminSection>
        )}
        {isAdmin && (
          <CollapsibleAdminSection
            title="Free-score audits (legacy)"
            description="Legacy free-tier profile optimizations."
            icon={<FileText size={18} className="text-primary" />}
            testId="admin-section-free-audits"
          >
            <FreeScoreAuditsPanel />
          </CollapsibleAdminSection>
        )}

        {isAdmin && <SupportTicketsPanel />}

        {isAdmin && <TrackingScriptsPanel />}

        {isAdmin && (
          <CollapsibleAdminSection
            title="Post Writer Trainer"
            description="Curate house style rules + good/bad exemplar posts. Buyer thumbs feedback auto-promotes into this corpus."
            icon={<BookOpen size={18} className="text-primary" />}
            testId="admin-section-post-writer-trainer"
          >
            <PostWriterTrainerPanel />
          </CollapsibleAdminSection>
        )}

        {isAdmin && (
          <CollapsibleAdminSection
            title="Inspiration leaders"
            description="Curated inspiration profiles library."
            icon={<Layers size={18} className="text-primary" />}
            testId="admin-section-inspirations"
          >
            <AdminInspirationsPanel />
          </CollapsibleAdminSection>
        )}

        {isAdmin && (
          <CollapsibleAdminSection
            title="Expert-audit preferences"
            description="Users' answers to the 7-question expert-audit questionnaire — feeds their profile rewrite."
            icon={<FileText size={18} className="text-primary" />}
            testId="admin-section-expert-prefs"
          >
            <AdminExpertPreferencesPanel />
          </CollapsibleAdminSection>
        )}

        {isAdmin && (
          <CollapsibleAdminSection
            title="Consent logs"
            description="Compliance audit trail — consent events."
            icon={<ShieldCheck size={18} className="text-primary" />}
            testId="admin-section-consent-logs"
          >
            <ConsentLogPanel />
          </CollapsibleAdminSection>
        )}

        <Card className="border-border">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-center gap-2">
              <Users size={18} className="text-primary" />
              <h2 className="font-semibold">All users</h2>
              <span className="text-sm text-muted-foreground ml-auto" data-testid="admin-users-count">{users.length} total</span>
            </div>
            {selectedIds.size > 0 && (
              <div
                className="px-4 py-2.5 bg-primary/8 border-b border-primary/20 flex items-center gap-3"
                data-testid="admin-bulk-toolbar"
              >
                <Sparkles size={14} className="text-primary" />
                <span className="text-[12px] font-medium">
                  {selectedIds.size} recipient{selectedIds.size === 1 ? '' : 's'} selected
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelected}
                    className="text-[11px]"
                    data-testid="admin-bulk-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setBulkOpen(true)}
                    className="gap-1.5"
                    data-testid="admin-bulk-send-open"
                  >
                    <Sparkles size={13} />
                    Send comp audit to {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'}
                  </Button>
                </div>
              </div>
            )}
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 size={20} className="animate-spin inline" />
              </div>
            ) : (
              <Table data-testid="admin-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      {bulkEligibleUsers.length > 0 && (
                        <Checkbox
                          checked={allBulkSelected}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all eligible users"
                          data-testid="admin-bulk-select-all"
                        />
                      )}
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-center">Analyses</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid="admin-user-row">
                      <TableCell className="w-8">
                        {(u.analyses_count || 0) > 0 && (
                          <Checkbox
                            checked={selectedIds.has(u.id)}
                            onCheckedChange={() => toggleSelected(u.id)}
                            aria-label={`Select ${u.email}`}
                            data-testid={`admin-bulk-select-${u.email}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {u.name}
                        {u.role && u.role !== 'viewer' && (
                          <RoleBadge role={u.role} className="ml-2" />
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={`${roleBadge(u.role)} font-mono-edge text-[11px]`} data-testid={`admin-user-role-${u.email}`}>{u.role}</Badge>
                      </TableCell>
                      <TableCell className="text-center font-mono-edge">{u.analyses_count || 0}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : ''}</TableCell>
                      <TableCell className="text-right">
                        {(u.analyses_count || 0) > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={exportingReportFor === u.id}
                                aria-label="Export branded audit report"
                                title="Export branded audit report (PDF)"
                                data-testid={`admin-export-report-${u.email}`}
                              >
                                {exportingReportFor === u.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <FileDown size={14} />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              data-testid={`admin-export-report-menu-${u.email}`}
                              className="w-72"
                            >
                              <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-muted-foreground">
                                BRANDED AUDIT REPORT
                              </DropdownMenuLabel>

                              <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-primary pt-2 pb-1">
                                CLIENT PREVIEW (REDACTED)
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => onExportBrandedReport(u, 'partial', 'pdf')}
                                data-testid={`admin-export-report-partial-pdf-${u.email}`}
                              >
                                <FileText size={14} className="mr-2 text-primary" />
                                <div className="flex flex-col flex-1">
                                  <span>Partial preview · PDF</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    Scores + gaps. Suggestions redacted.
                                  </span>
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => onExportBrandedReport(u, 'partial', 'pptx')}
                                data-testid={`admin-export-report-partial-pptx-${u.email}`}
                              >
                                <Presentation size={14} className="mr-2 text-primary" />
                                <div className="flex flex-col flex-1">
                                  <span>Partial preview · PPTX</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    Editable PowerPoint deck (16:9).
                                  </span>
                                </div>
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-primary pt-1 pb-1">
                                FULL REPORT
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => onExportBrandedReport(u, 'full', 'pdf')}
                                data-testid={`admin-export-report-full-pdf-${u.email}`}
                              >
                                <FileDown size={14} className="mr-2 text-primary" />
                                <div className="flex flex-col flex-1">
                                  <span>Full report · PDF</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    Everything: rewrites, peers, topics.
                                  </span>
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => onExportBrandedReport(u, 'full', 'pptx')}
                                data-testid={`admin-export-report-full-pptx-${u.email}`}
                              >
                                <Presentation size={14} className="mr-2 text-primary" />
                                <div className="flex flex-col flex-1">
                                  <span>Full report · PPTX</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    Editable widescreen slides for client meetings.
                                  </span>
                                </div>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dashboard?owner=${encodeURIComponent(u.id)}`)}
                          aria-label="View this user's analyses"
                          title={`View ${u.name || u.email}'s analyses`}
                          data-testid={`admin-view-analyses-${u.email}`}
                        >
                          <FolderOpen size={14} className="text-primary" />
                        </Button>
                        {(u.analyses_count || 0) > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCompAuditTarget(u)}
                            aria-label="Send complimentary audit magic link"
                            title="Send this user a magic link to their latest audit"
                            className="text-primary hover:text-primary/80"
                            data-testid={`admin-send-comp-${u.email}`}
                          >
                            <Sparkles size={14} />
                          </Button>
                        )}
                        {isAdmin && u.role === 'editor' && (
                          <Button variant="ghost" size="sm" onClick={() => setAssignTarget(u)} aria-label="Assign users" title="Assign users this editor can access" data-testid={`admin-assign-user-${u.email}`}>
                            <UsersIcon size={14} />
                          </Button>
                        )}
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditTarget(u)} aria-label="Edit user" data-testid={`admin-edit-user-${u.email}`}>
                              <Pencil size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)} aria-label="Reset password" title="Reset user's password" data-testid={`admin-reset-pw-${u.email}`}>
                              <KeyRound size={14} />
                            </Button>
                            {u.id !== user.id && (
                              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(u)} aria-label="Delete user" data-testid={`admin-delete-user-${u.email}`}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <AddUserDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={reload} />
        <EditUserDialog user={editTarget} onClose={() => setEditTarget(null)} onSaved={reload} />
        <AssignUsersDialog editor={assignTarget} allUsers={users} onClose={() => setAssignTarget(null)} onSaved={reload} />

        {/* Confirm-reset prompt */}
        <AlertDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
          <AlertDialogContent data-testid="admin-reset-pw-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset password for {resetTarget?.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                We&rsquo;ll generate a new 12-character temporary password and show it to you
                once. Copy it and share it with the user via a secure channel &mdash; they
                should change it after logging in.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-reset-pw-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirmReset}
                disabled={resetSubmitting}
                data-testid="admin-reset-pw-confirm-btn"
              >
                {resetSubmitting ? <Loader2 size={14} className="animate-spin" /> : 'Generate new password'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reveal result dialog (shows temp password ONCE) */}
        <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
          <DialogContent className="sm:max-w-md" data-testid="admin-reset-pw-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound size={16} className="text-primary" /> New password ready
              </DialogTitle>
              <DialogDescription>
                Copy this now &mdash; we won&rsquo;t show it again. Share with{' '}
                <span className="font-medium text-foreground">{resetResult?.email}</span> via a
                secure channel.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-2.5">
              <code className="font-mono-edge flex-1 text-sm" data-testid="admin-reset-pw-value">
                {resetResult?.temp_password}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(resetResult?.temp_password || '')}
                data-testid="admin-reset-pw-copy"
              >
                <Copy size={14} className="mr-1.5" /> Copy
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => setResetResult(null)} data-testid="admin-reset-pw-done">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deleteTarget?.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the user, their analyses, and any pending jobs. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="admin-delete-confirm"
                onClick={async () => {
                  try {
                    await adminApi.deleteUser(deleteTarget.id);
                    toast.success('User deleted');
                    setDeleteTarget(null);
                    reload();
                  } catch (err) {
                    toast.error(getErrorMessage(err, 'Delete failed'));
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <SendCompAuditDialog
          userId={compAuditTarget?.id}
          defaultEmail={compAuditTarget?.email || ''}
          defaultName={compAuditTarget?.name || ''}
          profileDisplay={compAuditTarget?.name || compAuditTarget?.email || ''}
          open={!!compAuditTarget}
          onOpenChange={(o) => { if (!o) setCompAuditTarget(null); }}
        />
        <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) closeBulkDialog(); else setBulkOpen(true); }}>
          <DialogContent className="sm:max-w-2xl" data-testid="admin-bulk-send-dialog">
            <DialogHeader>
              <DialogTitle>
                Send comp audit to {selectedIds.size} recipient{selectedIds.size === 1 ? '' : 's'}
              </DialogTitle>
              <DialogDescription>
                Each recipient gets their own magic link to their most recent completed audit.
                Link expires in 7 days per recipient. Existing accounts get a magic-link login;
                new accounts are created on the fly.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-1">
              <div>
                <Label htmlFor="bulk-msg" className="text-[12px]">
                  Personal note <span className="text-gray-400 text-[10px]">(optional — appears in every email)</span>
                </Label>
                <Textarea
                  id="bulk-msg"
                  rows={3}
                  placeholder="Sharing a quick complimentary audit — happy to jump on a call if you'd like a walkthrough."
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  className="resize-none mt-1.5"
                  disabled={bulkSending}
                  data-testid="admin-bulk-message"
                />
              </div>
              <div className="max-h-52 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-[12px]">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr className="text-left text-[10px] font-mono-edge tracking-wider uppercase text-muted-foreground">
                      <th className="px-3 py-1.5">Recipient</th>
                      <th className="px-3 py-1.5 text-center">Analyses</th>
                      <th className="px-3 py-1.5 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {Array.from(selectedIds).map((uid) => {
                      const u = users.find((x) => x.id === uid);
                      const row = (bulkResult?.results || []).find((r) => r.user_id === uid);
                      return (
                        <tr key={uid} data-testid={`admin-bulk-row-${u?.email || uid}`}>
                          <td className="px-3 py-1.5">
                            <div className="font-medium">{u?.name || u?.email || uid}</div>
                            <div className="text-[10px] text-muted-foreground">{u?.email || ''}</div>
                          </td>
                          <td className="px-3 py-1.5 text-center font-mono-edge">
                            {u?.analyses_count || 0}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {!row && <span className="text-muted-foreground">—</span>}
                            {row?.ok && (
                              <span className="text-emerald-600 font-mono-edge text-[10px] uppercase tracking-wider">
                                ✓ Sent
                              </span>
                            )}
                            {row && !row.ok && (
                              <span className="text-red-600 font-mono-edge text-[10px] uppercase tracking-wider" title={row.detail || ''}>
                                ✗ {row.reason || 'failed'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {bulkResult && (
                <div className={[
                  'text-[12px] rounded-md px-3 py-2',
                  bulkResult.ok
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                    : 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
                ].join(' ')}>
                  {bulkResult.sent === bulkResult.total
                    ? `All ${bulkResult.total} recipients received the magic link.`
                    : `Sent to ${bulkResult.sent} of ${bulkResult.total} — check the table for failed rows.`}
                </div>
              )}
            </div>
            <DialogFooter className="!mt-4">
              <Button variant="outline" disabled={bulkSending} onClick={closeBulkDialog} data-testid="admin-bulk-cancel">
                {bulkResult ? 'Close' : 'Cancel'}
              </Button>
              {!bulkResult && (
                <Button onClick={submitBulkSend} disabled={bulkSending || selectedIds.size === 0} data-testid="admin-bulk-send-submit">
                  {bulkSending ? 'Sending…' : `Send to ${selectedIds.size}`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function RoleSummary({ role, count, desc }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Badge className={`${roleBadge(role)} font-mono-edge`}>{role}</Badge>
          <span className="text-2xl font-bold font-display">{count}</span>
        </div>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </CardContent>
    </Card>
  );
}

function AddUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('viewer');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setEmail(''); setName(''); setPassword(''); setRole('viewer'); };
  const close = () => { reset(); onClose(); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminApi.createUser({ email, name, password, role });
      toast.success('User created');
      close();
      onCreated && onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not create user'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a user</DialogTitle>
          <DialogDescription>They’ll log in with this email + password and be assigned the role you pick.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-email">Email</Label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-3 text-muted-foreground" />
              <Input id="new-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" className="pl-9" data-testid="admin-add-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-name">Name (optional)</Label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-3 text-muted-foreground" />
              <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Avery Chen" className="pl-9" data-testid="admin-add-name" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Temporary password</Label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-3 text-muted-foreground" />
              <Input id="new-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 chars" className="pl-9" data-testid="admin-add-password" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="admin-add-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin — full access</SelectItem>
                <SelectItem value="editor">Editor — see own + admin-assigned users</SelectItem>
                <SelectItem value="viewer">Viewer — only their own</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="rounded-xl" data-testid="admin-add-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Create user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [role, setRole] = useState(user?.role || 'viewer');
  const [name, setName] = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [isEnterprise, setIsEnterprise] = useState(!!user?.is_enterprise);
  const [adminFeatures, setAdminFeatures] = useState(Array.isArray(user?.admin_features) ? [...user.admin_features] : []);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setRole(user.role || 'viewer');
      setName(user.name || '');
      setPassword('');
      setIsEnterprise(!!user.is_enterprise);
      setAdminFeatures(Array.isArray(user.admin_features) ? [...user.admin_features] : []);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.listFeatureFlags();
        if (!cancelled) setFeatureCatalog(r?.features || []);
      } catch (err) {
        // Non-fatal — the dialog still opens without the checkbox row.
        if (!cancelled) console.warn('feature flag list failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;

  const toggleFeature = (key) => {
    setAdminFeatures((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const originalFeatures = Array.isArray(user.admin_features) ? [...user.admin_features].sort() : [];
  const currentFeaturesSorted = [...adminFeatures].sort();
  const featuresChanged = originalFeatures.join('|') !== currentFeaturesSorted.join('|');

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {};
    if (role !== user.role) payload.role = role;
    if (name !== user.name) payload.name = name;
    if (password) payload.password = password;
    if (isEnterprise !== !!user.is_enterprise) payload.is_enterprise = isEnterprise;
    if (featuresChanged) payload.admin_features = adminFeatures;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    try {
      await adminApi.updateUser(user.id, payload);
      toast.success('User updated');
      onClose();
      onSaved && onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Update failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const isAdminRole = role === 'admin';

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {user.email}</DialogTitle>
          <DialogDescription>Change role, name, password, or delegated admin access.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-edit-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="admin-edit-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Reset password (optional)</Label>
            <Input type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" data-testid="admin-edit-password" />
          </div>
          <div className="space-y-1.5 pt-1 border-t border-border">
            <div className="flex items-center justify-between gap-3 pt-2">
              <div className="min-w-0">
                <Label className="cursor-pointer" htmlFor="admin-edit-enterprise">Enterprise access</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Unlocks Peer Benchmark (and future Enterprise-only tabs) across all of this user&apos;s audits.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isEnterprise}
                id="admin-edit-enterprise"
                onClick={() => setIsEnterprise((v) => !v)}
                data-testid="admin-edit-enterprise-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${isEnterprise ? 'bg-primary' : 'bg-secondary'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnterprise ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
          {featureCatalog.length > 0 && (
            <div className="space-y-2 pt-3 border-t border-border" data-testid="admin-edit-features-section">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label>Delegated admin access</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Grant this user specific admin surfaces without promoting them to Admin. Ignored when role is already Admin.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-xs"
                  onClick={() => setHistoryOpen(true)}
                  data-testid="admin-edit-grant-history-btn"
                >
                  History
                </Button>
              </div>
              <div className="space-y-2 rounded-lg border border-border p-3">
                {featureCatalog.map((f) => {
                  const checked = adminFeatures.includes(f.key);
                  return (
                    <label
                      key={f.key}
                      htmlFor={`admin-feature-${f.key}`}
                      className={`flex items-start gap-3 cursor-pointer ${isAdminRole ? 'opacity-50' : ''}`}
                      data-testid={`admin-edit-feature-row-${f.key}`}
                    >
                      <Checkbox
                        id={`admin-feature-${f.key}`}
                        checked={checked}
                        onCheckedChange={() => toggleFeature(f.key)}
                        disabled={isAdminRole}
                        data-testid={`admin-edit-feature-${f.key}`}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{f.label}</p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">{f.description}</p>
                      </div>
                    </label>
                  );
                })}
                {isAdminRole && (
                  <p className="text-[11px] text-muted-foreground italic pt-1">
                    Admins already have access to every surface — grants are ignored.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="rounded-xl" data-testid="admin-edit-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {historyOpen && (
        <GrantHistoryDialog user={user} onClose={() => setHistoryOpen(false)} />
      )}
    </Dialog>
  );
}


function GrantHistoryDialog({ user, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.getGrantLog({ target_user_id: user.id, limit: 50 });
        if (!cancelled) setItems(r?.items || []);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, 'Could not load grant history'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const fmt = (ts) => {
    try { return format(new Date(ts), 'MMM d, yyyy · HH:mm'); } catch (_e) { return ts; }
  };

  const summarize = (row) => {
    if (row.field === 'admin_features') {
      const added = row.added || [];
      const removed = row.removed || [];
      const parts = [];
      if (added.length) parts.push(`granted ${added.join(', ')}`);
      if (removed.length) parts.push(`revoked ${removed.join(', ')}`);
      return parts.join(' · ') || 'no change';
    }
    if (row.field === 'password') return 'password reset';
    return `${row.field}: ${row.before ?? '—'} → ${row.after ?? '—'}`;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="admin-grant-history-dialog">
        <DialogHeader>
          <DialogTitle>Change history · {user.email}</DialogTitle>
          <DialogDescription>Every role, admin-grant, enterprise, name and password change on this user, newest first.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="admin-grant-history-empty">
            No admin actions recorded for this user yet.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="admin-grant-history-list">
            {items.map((row) => (
              <li key={row.id} className="py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium truncate">
                    <span className="font-mono-edge text-[11px] uppercase tracking-[0.14em] text-primary mr-2">
                      {row.field}
                    </span>
                    {summarize(row)}
                  </p>
                  <p className="text-[10px] font-mono-edge text-muted-foreground whitespace-nowrap">
                    {fmt(row.at)}
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  by <span className="font-medium">{row.actor_email || row.actor_id}</span>
                  {row.actor_role ? <span className="ml-1 text-[10px] uppercase tracking-[0.12em]">({row.actor_role})</span> : null}
                </p>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AssignUsersDialog({ editor, allUsers, onClose, onSaved }) {
  const [assignedIds, setAssignedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!editor) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { assigned_user_ids } = await adminApi.getAssignedUsers(editor.id);
        if (!cancelled) setAssignedIds(assigned_user_ids || []);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, 'Could not load assignments'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setSearch('');
    load();
    return () => { cancelled = true; };
  }, [editor]);

  if (!editor) return null;

  // Show every user except the editor themselves and other admins
  // (assignments are only meaningful for non-admin targets that the editor
  // would otherwise not see). We still surface viewers + other editors.
  const candidates = (allUsers || []).filter((u) => u.id !== editor.id && u.role !== 'admin');
  const filtered = candidates.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  const toggle = (uid) => {
    setAssignedIds((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const save = async () => {
    setSubmitting(true);
    try {
      await adminApi.setAssignedUsers(editor.id, assignedIds);
      toast.success(`Updated access for ${editor.email}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save assignments'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = assignedIds.length;

  return (
    <Dialog open={!!editor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersIcon size={16} className="text-primary" />
            Assign users to {editor.name || editor.email}
          </DialogTitle>
          <DialogDescription>
            Pick which users this editor can view and edit. Editors automatically see their own
            analyses; only assigned users show up beyond that.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              data-testid="admin-assign-search"
            />
            <Badge variant="secondary" className="font-mono-edge shrink-0" data-testid="admin-assign-selected-count">
              {selectedCount} selected
            </Badge>
          </div>

          <div className="rounded-md border border-border">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 size={18} className="animate-spin inline" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {candidates.length === 0
                  ? 'No assignable users yet. Add viewers or other editors first.'
                  : 'No users match your search.'}
              </div>
            ) : (
              <ScrollArea className="h-72">
                <ul className="divide-y divide-border">
                  {filtered.map((u) => {
                    const checked = assignedIds.includes(u.id);
                    return (
                      <li key={u.id}>
                        <label
                          htmlFor={`assign-${u.id}`}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50 transition-colors"
                          data-testid={`admin-assign-row-${u.email}`}
                        >
                          <Checkbox
                            id={`assign-${u.id}`}
                            checked={checked}
                            onCheckedChange={() => toggle(u.id)}
                            data-testid={`admin-assign-checkbox-${u.email}`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{u.name || u.email}</span>
                              <Badge className={`${roleBadge(u.role)} font-mono-edge text-[10px] shrink-0`}>
                                {u.role}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                          {checked && <Check size={14} className="text-primary shrink-0" />}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="admin-assign-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={submitting || loading}
            className="rounded-xl"
            data-testid="admin-assign-save"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Save assignments'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
